import { useServices } from 'features/service/hooks/useServices';
import { IndianRupee, ListChecks, FolderTree } from 'lucide-react';

const getApprovalStatusColor = (status) => {
  const colors = {
    pending: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
    approved: 'bg-green-500/10 text-green-600 dark:text-green-400',
    rejected: 'bg-red-500/10 text-red-600 dark:text-red-400',
  };
  return colors[status] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
};

const getImageUrl = (images) => {
  if (!images || images.length === 0) return null;
  const primary = images.find((img) => img?.isPrimary) || images[0];
  return primary?.url || null;
};

const UNCATEGORIZED = 'Uncategorized';

// Groups services into { [categoryName]: { [subcategoryName]: [service, ...] } },
// preserving first-seen order for both category and subcategory keys.
const groupByCategory = (services) => {
  const groups = new Map();

  services.forEach((service) => {
    const categoryName =
      service.categoryId?.name?.english || service.category?.name?.english || UNCATEGORIZED;
    const subcategoryName =
      service.subcategoryId?.name?.english || service.subcategory?.name?.english || UNCATEGORIZED;

    if (!groups.has(categoryName)) {
      groups.set(categoryName, new Map());
    }
    const subGroups = groups.get(categoryName);

    if (!subGroups.has(subcategoryName)) {
      subGroups.set(subcategoryName, []);
    }
    subGroups.get(subcategoryName).push(service);
  });

  return groups;
};

const ServiceCard = ({ service }) => {
  const imageUrl = getImageUrl(service.images);
  return (
    <div className='rounded-lg border border-border bg-card p-4'>
      <div className='flex items-start gap-3 mb-3'>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={service.name}
            className='w-12 h-12 rounded-lg object-cover border border-border flex-shrink-0'
          />
        ) : (
          <div className='w-12 h-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0'>
            <ListChecks size={18} className='text-muted-foreground' />
          </div>
        )}
        <div className='min-w-0'>
          <p className='text-sm font-medium truncate'>{service.name || 'N/A'}</p>
          {service.duration && (
            <p className='text-xs text-muted-foreground'>{service.duration} min</p>
          )}
        </div>
      </div>

      <div className='flex items-center justify-between'>
        <div className='flex items-center text-sm font-semibold'>
          <IndianRupee size={14} className='mr-1' />
          {service.pricing?.basePrice ?? service.price ?? 'N/A'}
        </div>
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getApprovalStatusColor(
            service.approvalStatus
          )}`}
        >
          {service.approvalStatus || 'N/A'}
        </span>
      </div>
    </div>
  );
};

const ServicesTab = ({ sellerId }) => {
  const { data, isPending, isError, error } = useServices({
    sellerId,
    page: '1',
    limit: '50',
    isActive: 'all',
    approvalStatus: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const services = data?.data || [];
  const grouped = groupByCategory(services);

  return (
    <div className='bg-card border border-border rounded-lg p-6'>
      <div className='flex items-center justify-between mb-4'>
        <h2 className='text-lg font-semibold flex items-center'>
          <ListChecks className='mr-2' size={20} />
          Services
        </h2>
        <span className='text-xs text-muted-foreground'>
          {services.length} {services.length === 1 ? 'service' : 'services'} across{' '}
          {grouped.size} {grouped.size === 1 ? 'category' : 'categories'}
        </span>
      </div>

      {isPending ? (
        <p className='text-sm text-muted-foreground'>Loading services...</p>
      ) : isError ? (
        <p className='text-sm text-destructive'>
          Error loading services: {error?.message || 'Something went wrong'}
        </p>
      ) : services.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          This provider has not selected any services from the catalog yet.
        </p>
      ) : (
        <div className='space-y-6'>
          {Array.from(grouped.entries()).map(([categoryName, subGroups]) => (
            <div key={categoryName}>
              <div className='flex items-center gap-2 mb-3 pb-2 border-b border-border'>
                <FolderTree size={16} className='text-primary' />
                <h3 className='text-sm font-semibold text-foreground'>{categoryName}</h3>
                <span className='text-xs text-muted-foreground'>
                  ({Array.from(subGroups.values()).reduce((sum, list) => sum + list.length, 0)})
                </span>
              </div>

              <div className='space-y-4 pl-1'>
                {Array.from(subGroups.entries()).map(([subcategoryName, subServices]) => (
                  <div key={subcategoryName}>
                    <div className='flex items-center gap-2 mb-2'>
                      <span className='text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                        {subcategoryName}
                      </span>
                      <span className='text-xs text-muted-foreground'>({subServices.length})</span>
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                      {subServices.map((service) => (
                        <ServiceCard key={service._id} service={service} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServicesTab;
